import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  PlusCircle,
  X,
  Save,
  ArrowLeft,
  Tag,
  Activity,
  Info,
  Trash2,
  Upload,
  Loader2,
  Plus,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ReactSelect } from "@/components/ui/react-select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../Router/all_routes";
import DefaultEditor from "react-simple-wysiwyg";

const PredefinedRepairForm = ({
  initialData,
  onSubmit,
  isSubmitting,
  onCancel,
}) => {
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };
  const navigate = useNavigate();


  const formatProductLabel = (p) => {
    if (!p) return "";
    return `${p.name}${p.sku ? ` [${p.sku}]` : ""}`;
  };

  const formatEmployeeLabel = (e) => {
    if (!e) return "";
    return e.full_name || e.name || "Select Employee";
  };

  const [productSearchTerm, setProductSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    id: null,
    name: "",
    repair_category_ids: [],
    item_description: "",
    issue_description: "",
    diagnosis: "",
    estimated_cost: 0,
    priority: "normal",
    estimated_completion_date: "",
    customer_notes: "",
    technician_notes: "",
    is_active: true,
    sort_order: 0,
    parts_n_components: [],
    labor_n_timetracking: [],
  });

  useEffect(() => {
    if (initialData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormData({
        ...initialData,
        name: initialData.name || "",
        item_description: initialData.item_description || "",
        issue_description: initialData.issue_description || "",
        diagnosis: initialData.diagnosis || "",
        estimated_cost: initialData.estimated_cost || 0,
        estimated_completion_date: initialData.estimated_completion_date || "",
        customer_notes: initialData.customer_notes || "",
        technician_notes: initialData.technician_notes || "",
        priority: initialData.priority || "low",
        is_active: initialData.is_active ?? true,
        repair_category_ids:
          initialData.repair_category_ids ||
          initialData.repair_categories?.map((c) => c.id) ||
          [],
        parts_n_components: (initialData.parts_n_components || []).map(
          (part) => ({
            ...part,
            part_id: part.part_id || "",
            quantity: part.quantity || 1,
            cost: part.cost || 0,
            notes: part.notes || "",
          }),
        ),
        labor_n_timetracking: (initialData.labor_n_timetracking || []).map(
          (labor) => ({
            lab_id: labor.lab_id || String(Date.now() + Math.random()),
            labor_id: labor.labor_id || labor.employee_id || "",
            hourly_rate: labor.hourly_rate ?? labor.rate ?? 0,
            description: labor.description || labor.notes || "",
          }),
        ),
      });
    }
  }, [initialData]);

  const { data: repairCategories = [] } = useQuery({
    queryKey: ["repair-categories"],
    queryFn: async () => {
      const res = await apiClient.get(`/category_repair`);
      return res.data.data || [];
    },
  });

  const { data: products = [], isFetching: productsLoading } = useQuery({
    queryKey: ["products", productSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(`/product`, {
        params: {
          search: productSearchTerm,
          per_page: 50,
          with: ["unit", "categories"],
        },
      });
      return res.data.data || [];
    },
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const res = await apiClient.get(`/employee`);
      return res.data.data || [];
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Main Content Area */}
      <div className="lg:col-span-3 space-y-6">
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="general">Details & Cost</TabsTrigger>
            <TabsTrigger value="descriptions">Descriptions</TabsTrigger>
            <TabsTrigger value="parts">Parts & Components</TabsTrigger>
            <TabsTrigger value="labor">Labor & Time</TabsTrigger>
          </TabsList>

          <div className="mt-6">
            <TabsContent value="general" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 font-sans font-bold">
                    <Info className="h-4 w-4 text-primary" />
                    Template Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Template Name *</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g., Screen Replacement - iPhone 13"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Estimated Base Cost</Label>
                      <Input
                        name="estimated_cost"
                        type="number"
                        step="0.01"
                        value={formData.estimated_cost}
                        onChange={(e) =>
                          handleSelectChange(
                            "estimated_cost",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Estimated Completion Date</Label>
                      <Input
                        name="estimated_completion_date"
                        type="datetime-local"
                        value={formData.estimated_completion_date}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 font-sans font-bold">
                    <Activity className="h-4 w-4 text-primary" />
                    Standard Notes
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Customer Facing Notes</Label>
                    <DefaultEditor
                      value={formData.customer_notes}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customer_notes: e.target.value,
                        })
                      }
                      className="min-h-[160px] rounded-md border-border overflow-hidden"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Internal Technician Notes</Label>
                    <DefaultEditor
                      value={formData.technician_notes}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          technician_notes: e.target.value,
                        })
                      }
                      className="min-h-[160px] rounded-md border-border overflow-hidden"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="descriptions" className="space-y-4">
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="space-y-2">
                    <Label>Standard Item Description *</Label>
                    <Input
                      name="item_description"
                      value={formData.item_description}
                      onChange={handleChange}
                      placeholder="e.g., Apple iPhone 13 Pro Max"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Standard Issue Description *</Label>
                    <DefaultEditor
                      value={formData.issue_description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          issue_description: e.target.value,
                        })
                      }
                      className="min-h-[200px] rounded-md border-border overflow-hidden"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Standard Diagnosis / Solution</Label>
                    <DefaultEditor
                      value={formData.diagnosis}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          diagnosis: e.target.value,
                        })
                      }
                      className="min-h-[200px] rounded-md border-border overflow-hidden"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="parts" className="space-y-4">
              <div className="flex justify-between items-center px-1">
                <h3 className="text-lg font-medium font-sans">
                  Predefined Parts
                </h3>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      parts_n_components: [
                        ...prev.parts_n_components,
                        { part_id: "", quantity: 1, cost: 0, notes: "" },
                      ],
                    }));
                  }}
                >
                  <PlusCircle className="mr-2 h-4 w-4" /> Add Part
                </Button>
              </div>

              {formData.parts_n_components.length === 0 ? (
                <Card className="bg-muted/30 border-dashed">
                  <CardContent className="py-10 text-center text-muted-foreground italic font-sans">
                    No parts predefined for this template.
                  </CardContent>
                </Card>
              ) : (
                formData.parts_n_components.map((part, index) => (
                  <Card key={index} className="relative overflow-hidden">
                    <CardContent className="pt-6">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-2 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                        onClick={() => {
                          const updatedParts =
                            formData.parts_n_components.filter(
                              (_, i) => i !== index,
                            );
                          setFormData({
                            ...formData,
                            parts_n_components: updatedParts,
                          });
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-2 space-y-2">
                          <Label>Part</Label>
                          <ReactSelect
                            placeholder="Search part..."
                            options={products.map((p) => ({
                              value: p.id,
                              label: formatProductLabel(p),
                            }))}
                            onInputChange={(val) => setProductSearchTerm(val)}
                            isLoading={productsLoading}
                            value={
                              part.part_id
                                ? products.find(
                                  (p) =>
                                    String(p.id) === String(part.part_id),
                                )
                                  ? {
                                    value: part.part_id,
                                    label: formatProductLabel(
                                      products.find(
                                        (p) =>
                                          String(p.id) ===
                                          String(part.part_id),
                                      ),
                                    ),
                                  }
                                  : part.product
                                    ? {
                                      value: part.part_id,
                                      label: formatProductLabel(part.product),
                                    }
                                    : null
                                : null
                            }
                            onChange={(option) => {
                              const updatedParts = [
                                ...formData.parts_n_components,
                              ];
                              const product = products.find(
                                (p) => String(p.id) === String(option?.value),
                              );
                              updatedParts[index].part_id = option?.value || "";
                              if (product) {
                                updatedParts[index].cost =
                                  parseFloat(product.sell_price) || 0;
                                updatedParts[index].product = product;
                              }
                              setFormData({
                                ...formData,
                                parts_n_components: updatedParts,
                              });
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Quantity</Label>
                          <Input
                            type="number"
                            value={part.quantity}
                            onChange={(e) => {
                              const updatedParts = [
                                ...formData.parts_n_components,
                              ];
                              updatedParts[index].quantity =
                                parseInt(e.target.value) || 1;
                              setFormData({
                                ...formData,
                                parts_n_components: updatedParts,
                              });
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Est. Cost</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={part.cost}
                            onChange={(e) => {
                              const updatedParts = [
                                ...formData.parts_n_components,
                              ];
                              updatedParts[index].cost =
                                parseFloat(e.target.value) || 0;
                              setFormData({
                                ...formData,
                                parts_n_components: updatedParts,
                              });
                            }}
                          />
                        </div>
                        <div className="md:col-span-4 space-y-2">
                          <Label>Internal Notes for this Part</Label>
                          <Input
                            value={part.notes}
                            placeholder="Special instructions for this part..."
                            onChange={(e) => {
                              const updatedParts = [
                                ...formData.parts_n_components,
                              ];
                              updatedParts[index].notes = e.target.value;
                              setFormData({
                                ...formData,
                                parts_n_components: updatedParts,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="labor" className="space-y-4">
              <div className="flex justify-between items-center px-1">
                <h3 className="text-lg font-medium font-sans">
                  Predefined Labor
                </h3>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      labor_n_timetracking: [
                        ...prev.labor_n_timetracking,
                        {
                          lab_id: String(Date.now()),
                          labor_id: "",
                          hourly_rate: 0,
                          description: "",
                        },
                      ],
                    }));
                  }}
                >
                  <PlusCircle className="mr-2 h-4 w-4" /> Add Labor
                </Button>
              </div>

              {formData.labor_n_timetracking.length === 0 ? (
                <Card className="bg-muted/30 border-dashed">
                  <CardContent className="py-10 text-center text-muted-foreground italic font-sans">
                    No labor items predefined for this template.
                  </CardContent>
                </Card>
              ) : (
                formData.labor_n_timetracking.map((labor, index) => (
                  <Card key={index} className="relative overflow-hidden">
                    <CardContent className="pt-6">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-2 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                        onClick={() => {
                          const updatedLabor =
                            formData.labor_n_timetracking.filter(
                              (_, i) => i !== index,
                            );
                          setFormData({
                            ...formData,
                            labor_n_timetracking: updatedLabor,
                          });
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-2 space-y-2">
                          <Label>Role/Technician</Label>
                          <ReactSelect
                            options={employees.map((e) => ({
                              value: String(e.id),
                              label: formatEmployeeLabel(e),
                              hourly_rate: e.hourly_rate,
                            }))}
                            value={
                              labor.labor_id
                                ? employees
                                  .filter(
                                    (e) =>
                                      String(e.id) === String(labor.labor_id),
                                  )
                                  .map((e) => ({
                                    value: String(e.id),
                                    label: formatEmployeeLabel(e),
                                  }))[0] || null
                                : null
                            }
                            onChange={(option) => {
                              const updatedLabor = [
                                ...formData.labor_n_timetracking,
                              ];
                              const employee = employees.find(
                                (e) => String(e.id) === String(option?.value),
                              );
                              updatedLabor[index].labor_id =
                                option?.value || "";
                              // Auto-fill hourly rate from employee record
                              if (employee?.hourly_rate) {
                                updatedLabor[index].hourly_rate =
                                  employee.hourly_rate;
                              }
                              setFormData({
                                ...formData,
                                labor_n_timetracking: updatedLabor,
                              });
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Hourly Rate</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={labor.hourly_rate}
                            onChange={(e) => {
                              const updatedLabor = [
                                ...formData.labor_n_timetracking,
                              ];
                              updatedLabor[index].hourly_rate =
                                parseFloat(e.target.value) || 0;
                              setFormData({
                                ...formData,
                                labor_n_timetracking: updatedLabor,
                              });
                            }}
                          />
                        </div>
                        <div className="md:col-span-4 space-y-2">
                          <Label>Description</Label>
                          <Input
                            placeholder="e.g., Screen replacement labor"
                            value={labor.description}
                            onChange={(e) => {
                              const updatedLabor = [
                                ...formData.labor_n_timetracking,
                              ];
                              updatedLabor[index].description = e.target.value;
                              setFormData({
                                ...formData,
                                labor_n_timetracking: updatedLabor,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* Sidebar Column */}
      <div className="space-y-6">
        <div className="sticky top-6 space-y-6">
          {/* Actions Card */}
          <Card>
            <CardHeader className="flex flex-row items-center space-x-2 space-y-0">
              <Activity className="h-5 w-5 text-primary" />
              <CardTitle className="text-base font-sans font-bold">
                Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 font-sans font-medium">
              <div className="flex items-center justify-between text-sm py-2 border-b">
                <span className="text-muted-foreground">Status:</span>
                <Badge variant={formData.is_active ? "success" : "secondary"}>
                  {formData.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>

              <div className="space-y-2">
                <Button
                  className="w-full justify-center gap-2"
                  onClick={() => {
                    const cleanedData = {
                      ...formData,
                      parts_n_components: formData.parts_n_components.map(
                        // eslint-disable-next-line no-unused-vars
                        ({ product, ...part }) => part,
                      ),
                      labor_n_timetracking: formData.labor_n_timetracking.map(
                        // eslint-disable-next-line no-unused-vars
                        ({ lab_id, ...labor }) => labor,
                      ),
                    };
                    onSubmit(cleanedData);
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <PlusCircle className="animate-spin h-4 w-4" />
                  ) : (
                    <Save size={18} />
                  )}
                  {isSubmitting ? "Processing..." : "Save Template"}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-center gap-2 font-sans font-medium"
                  onClick={() =>
                    onCancel
                      ? onCancel()
                      : navigate(all_routes.predefinedRepairs)
                  }
                >
                  <X size={18} />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Categories Card */}
          <Card>
            <CardHeader className="flex flex-row items-center space-x-2 space-y-0">
              <Tag className="h-5 w-5 text-primary" />
              <CardTitle className="text-base font-sans font-bold">
                Categories
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold text-muted-foreground uppercase">
                  Repair Categories *
                </Label>
                <ReactSelect
                  isMulti
                  options={repairCategories.map((cat) => ({
                    value: cat.id,
                    label: cat.name,
                  }))}
                  value={repairCategories
                    .filter((cat) =>
                      formData.repair_category_ids.includes(cat.id),
                    )
                    .map((cat) => ({ value: cat.id, label: cat.name }))}
                  onChange={(selectedOptions) => {
                    setFormData({
                      ...formData,
                      repair_category_ids: selectedOptions
                        ? selectedOptions.map((opt) => opt.value)
                        : [],
                    });
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold text-muted-foreground uppercase">
                  Service Priority
                </Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) =>
                    handleSelectChange("priority", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id="is_active_form"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    handleCheckboxChange("is_active", checked)
                  }
                  disabled={
                    !hasPermission("predefined repair job toggle_active")
                  }
                />
                <Label
                  htmlFor="is_active_form"
                  className={`text-sm font-medium leading-none ${hasPermission("predefined repair job toggle_active")
                      ? "cursor-pointer"
                      : "cursor-not-allowed opacity-50"
                    }`}
                >
                  Template Active
                </Label>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PredefinedRepairForm;
